import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router';
import { Minus, Plus, Search } from 'lucide-react';
import { hoursShort, parseHours } from '@/lib/format';
import { cn } from './cn';

type Variant = 'primary' | 'dark' | 'secondary' | 'plain' | 'destructive';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-[var(--accent)] text-white dark:text-[var(--accent-fg)]',
  dark: 'bg-[var(--primary)] text-[var(--primary-fg)]',
  secondary: 'bg-[var(--surface)] text-[var(--text)] shadow-[inset_0_0_0_1px_var(--line)]',
  plain: 'text-[var(--accent)] font-medium',
  destructive: 'bg-red-50 text-[var(--danger)] dark:bg-red-950/50'
};

const BUTTON = 'inline-flex h-[50px] items-center justify-center gap-2 rounded-xl px-3 whitespace-nowrap text-body font-semibold transition-opacity active:opacity-60 disabled:opacity-40';

/** 50 tall, full width by default: on a phone a primary action spans the screen. */
export function Button({
  variant = 'primary',
  block = true,
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; block?: boolean }) {
  return <button type="button" {...props} className={cn(BUTTON, block && 'w-full', VARIANTS[variant], variant === 'plain' && 'h-11', className)} />;
}

export function LinkButton({ to, variant = 'primary', children, className }: { to: string; variant?: Variant; children: ReactNode; className?: string }) {
  return (
    <Link to={to} className={cn(BUTTON, 'w-full', VARIANTS[variant], className)}>
      {children}
    </Link>
  );
}

/** A nav bar text or icon button: 44pt hit area. */
export function BarButton({ className, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      {...props}
      className={cn('flex h-11 min-w-11 items-center justify-center px-2 text-body text-[var(--accent)] active:opacity-50 disabled:opacity-40', className)}
    />
  );
}

/** Segmented control, up to four segments. Replaces tabs and small radio groups. */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  className
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: ReactNode }[];
  className?: string;
}) {
  return (
    <div role="tablist" className={cn('grid h-9 rounded-[10px] bg-[var(--track)] p-0.5', className)} style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            'truncate rounded-[8px] px-1 text-[14px] transition-colors',
            value === o.value
              ? 'bg-[var(--surface)] font-semibold shadow-[0_1px_3px_rgba(0,0,0,0.12)] dark:bg-zinc-600'
              : 'font-medium text-zinc-700 dark:text-zinc-300'
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** More than four choices: a horizontally scrolling row of chips. */
export function ChipRow<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: { value: T; label: string }[] }) {
  return (
    <div className="-mx-4 flex gap-2 overflow-x-auto px-4 [scrollbar-width:none]">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            'h-[34px] shrink-0 rounded-full px-3.5 text-[14px] font-medium',
            value === o.value ? 'bg-[var(--primary)] text-[var(--primary-fg)]' : 'bg-[var(--surface)] shadow-[inset_0_0_0_1px_var(--line)]'
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Switch({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label?: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={cn('relative h-[31px] w-[51px] shrink-0 rounded-full transition-colors', checked ? 'bg-[var(--accent)]' : 'bg-[var(--track)]')}
    >
      <span className={cn('absolute top-0.5 left-0.5 size-[27px] rounded-full bg-white shadow-[0_2px_4px_rgba(0,0,0,0.2)] transition-transform', checked && 'translate-x-5')} />
    </button>
  );
}

export function SearchField({ value, onChange, placeholder = 'Search', autoFocus }: { value: string; onChange: (v: string) => void; placeholder?: string; autoFocus?: boolean }) {
  return (
    <label className="flex h-10 items-center gap-2 rounded-[10px] bg-[var(--track)] px-2.5">
      <Search className="size-[18px] shrink-0 text-[var(--text-subtle)]" />
      <input
        type="search"
        value={value}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="min-w-0 flex-1 bg-transparent text-body outline-none placeholder:text-[var(--text-subtle)]"
      />
    </label>
  );
}

/** Small summary tile: label, big number, optional caption. */
export function Stat({ label, value, caption, tone }: { label: string; value: ReactNode; caption?: ReactNode; tone?: 'overdue' | 'sent' }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-2xl bg-[var(--surface)] px-4 py-3.5">
      <span className="text-caption font-medium text-[var(--text-muted)]">{label}</span>
      <span className={cn('tnum text-[22px] leading-7 font-semibold', tone === 'overdue' && 'text-orange-700 dark:text-orange-300')}>{value}</span>
      {caption && <span className={cn('text-caption text-[var(--text-muted)]', tone === 'sent' && 'text-sky-700 dark:text-sky-300')}>{caption}</span>}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-[shimmer_1.4s_ease-in-out_infinite] rounded-lg bg-[var(--skeleton)]', className)} />;
}

/** Rows shaped like the real rows, so nothing jumps when data lands. */
export function SkeletonList({ rows = 5, bar }: { rows?: number; bar?: boolean }) {
  return (
    <div className="group overflow-hidden rounded-2xl bg-[var(--surface)]">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex h-16 items-center gap-3 px-4">
          {bar && <Skeleton className="h-9 w-1" />}
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-2/5" />
            <Skeleton className="h-3 w-3/5" />
          </div>
          <Skeleton className="h-4 w-12" />
        </div>
      ))}
    </div>
  );
}

/** Empty state: one icon, one line, one or two buttons. */
export function Empty({ icon, title, body, children }: { icon?: ReactNode; title: string; body?: string; children?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-2.5 rounded-2xl bg-[var(--surface)] px-6 py-10 text-center">
      {icon && (
        <span className="flex size-14 items-center justify-center rounded-full bg-[var(--accent-soft)] text-[var(--accent)] [&_svg]:size-7">{icon}</span>
      )}
      <span className="text-[20px] leading-7 font-semibold">{title}</span>
      {body && <span className="max-w-[260px] text-secondary leading-[1.45] text-[var(--text-muted)]">{body}</span>}
      {children && <div className="mt-2 flex w-full flex-col gap-2">{children}</div>}
    </div>
  );
}


const QUICK = [0.25, 0.5, 1, 1.5, 2];

/**
 * Hours: a big decimal field with a ±0.1 stepper and quick chips. Takes 1.5,
 * 1:30 or 90m as typed; the chips and stepper write decimals.
 */
export function HoursInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const parsed = parseHours(value);
  const step = (d: number) => onChange(hoursShort(Math.max(0, Math.round(((parsed ?? 0) + d) * 10) / 10)));
  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-center justify-between rounded-2xl bg-[var(--surface)] px-4 py-3.5">
        <button type="button" aria-label="0.1 hour less" onClick={() => step(-0.1)} className="flex size-11 items-center justify-center rounded-full bg-[var(--page)] active:opacity-60">
          <Minus className="size-5" />
        </button>
        <label className="flex items-baseline gap-1.5">
          <input
            aria-label="Hours"
            inputMode="decimal"
            placeholder="0"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="tnum w-28 bg-transparent text-right text-[44px] leading-[48px] font-semibold tracking-[-0.02em] outline-none placeholder:text-[var(--text-subtle)]"
          />
          <span className="text-body text-[var(--text-muted)]">hours</span>
        </label>
        <button type="button" aria-label="0.1 hour more" onClick={() => step(0.1)} className="flex size-11 items-center justify-center rounded-full bg-[var(--page)] active:opacity-60">
          <Plus className="size-5" />
        </button>
      </div>
      {value && parsed === null && <p className="px-4 text-caption text-[var(--danger)]">Try 1.5, 1:30 or 90m.</p>}
      <div className="grid grid-cols-5 gap-2">
        {QUICK.map((q) => (
          <button
            key={q}
            type="button"
            onClick={() => onChange(String(q))}
            className={cn(
              'tnum h-10 rounded-[10px] text-secondary',
              parsed === q ? 'bg-[var(--accent-soft)] font-semibold text-[var(--accent)]' : 'bg-[var(--surface)] font-medium'
            )}
          >
            {q}
          </button>
        ))}
      </div>
    </div>
  );
}
